/******************************************************************
 * ##: LocationTracker
 * App-level component that drives the single GPS watcher from the active service.
 * Starts tracking when a service is running (including automatically after a reload),
 * stops when the active service is cleared or when this component unmounts (logout),
 * and renders the discrete GPS status indicator. Sends best-effort telemetry only.
 *
 * NOTE: it does NOT stop on serviceStatus "completed" alone; the watcher must stay
 * alive so the service-end flow can capture the final position before stopping.
 *
 * TL;DR: Wires the active service to the location tracker + status chip.
 * History:
 * 23-07-2026: Created
 ******************************************************************/

import React, { useEffect, useMemo, useRef } from "react";
import { useAuth } from "services/Auth";
import useSelectedService from "hooks/useSelectedService";
import useLocationTracking from "hooks/useLocationTracking";
import { locationTracker } from "utils/location/geolocation";
import { offlineHandler } from "utils/offlineCache";
import { LOCATION_STATUS } from "constants/location";
import LocationStatusIndicator from "./LocationStatusIndicator";

export const isServiceRunning = (service) => {
  if (!service) return false;
  if (service.serviceStatus === "running") return true;
  return Boolean(service.start_date_effective) && !service.end_date_effective;
};

const getServiceContext = (service) => ({
  service_id_raw: service?.service_id_raw || null,
  equipment_id: service?.equipment?.[0]?._id || null,
});

const LocationTracker = () => {
  const { auth } = useAuth();
  const { selectedService } = useSelectedService();
  const { status, pendingCount } = useLocationTracking();
  const startedServiceRef = useRef(null);
  const deniedLoggedRef = useRef(false);

  const apiClient = useMemo(() => offlineHandler(auth?.data), [auth?.data]);
  const authUserName = auth?.data?.name || "Unknown User";

  // Start when a service is running; stop only when the active service is cleared.
  useEffect(() => {
    const context = getServiceContext(selectedService);
    const shouldTrack = isServiceRunning(selectedService);

    if (shouldTrack && context.service_id_raw) {
      if (startedServiceRef.current !== context.service_id_raw) {
        locationTracker.startTracking({ ...context, user: auth?.data?._id || null });
        startedServiceRef.current = context.service_id_raw;
        apiClient.post("/info-capture", {
          info: {
            location: "src/components/layout-components/LocationTracker/index.jsx/start",
            timestamp: new Date().toISOString(),
            service_id: context.service_id_raw,
            equipment: context.equipment_id,
          },
          subject: "Boats: Mobile Location",
          message: `Boats: Location tracking started (${authUserName})`,
        });
      }
    } else if (!selectedService && startedServiceRef.current) {
      locationTracker.stopTracking();
      startedServiceRef.current = null;
    }
  }, [selectedService, apiClient, authUserName, auth?.data?._id]);

  // Stop the watcher when this app-level component unmounts (e.g. logout).
  useEffect(() => {
    return () => {
      locationTracker.stopTracking();
    };
  }, []);

  // Log a denied permission once, without blocking anything.
  useEffect(() => {
    if (status === LOCATION_STATUS.DENIED && !deniedLoggedRef.current) {
      deniedLoggedRef.current = true;
      apiClient.post("/info-capture", {
        info: { location: "src/components/layout-components/LocationTracker/index.jsx/denied", timestamp: new Date().toISOString() },
        subject: "Boats: Mobile Location",
        message: `Boats: Location permission denied (${authUserName})`,
      });
    }
    if (status !== LOCATION_STATUS.DENIED) {
      deniedLoggedRef.current = false;
    }
  }, [status, apiClient, authUserName]);

  if (status === LOCATION_STATUS.IDLE && !isServiceRunning(selectedService)) {
    return null;
  }

  return <LocationStatusIndicator status={status} pendingCount={pendingCount} />;
};

export default LocationTracker;
